const yesNo = ["Yes", "No"];

const question = (
  id,
  section,
  label,
  { required = false, type = "text", options = null } = {},
) => ({
  id,
  section,
  label,
  required,
  type,
  options,
});

/** Fields shared across types that live on passengerItemInput */
const WAIVER = question("waiverSigned", "General", "Waiver Signed", {
  required: true,
  type: "select",
  options: yesNo,
});

export const ANIMAL_RESCUE_QUESTIONS = [
  question(
    "sendingOrganization",
    "Sending Organization",
    "Sending Organization",
  ),
  question(
    "sendingOrganizationAddress",
    "Sending Organization",
    "Sending Organization Address",
  ),
  question(
    "sendingOrganizationWebsite",
    "Sending Organization",
    "Sending Shelter / Rescue Website",
  ),
  question(
    "senderIs501c3",
    "Sending Organization",
    "Is the sender a 501(c)(3)?",
    {
      required: true,
      type: "select",
      options: yesNo,
    },
  ),
  question(
    "multipleSendingShelters",
    "Sending Organization",
    "Are there multiple sending shelters?",
    { required: true, type: "select", options: yesNo },
  ),
  question(
    "receiverOrganization",
    "Receiving Organization",
    "Receiver's Organization Name",
  ),
  question(
    "receiverFirstName",
    "Receiving Organization",
    "Receiver's Contact First Name",
    { required: true },
  ),
  question(
    "receiverLastName",
    "Receiving Organization",
    "Receiver's Contact Last Name",
    { required: true },
  ),
  question("receiverPhone", "Receiving Organization", "Receiver's Phone #", {
    required: true,
    type: "tel",
  }),
  question("receiverEmail", "Receiving Organization", "Receiver's Email", {
    required: true,
    type: "email",
  }),
  question(
    "receiverAddress",
    "Receiving Organization",
    "Receiver's Organization Address",
    { required: true },
  ),
  question(
    "receiverWebsite",
    "Receiving Organization",
    "Receiver's Shelter or Rescue Website",
    { required: true },
  ),
  question(
    "receiverIs501c3",
    "Receiving Organization",
    "Is the receiving shelter a 501(c)(3)?",
    { required: true, type: "select", options: yesNo },
  ),
  question(
    "multipleReceivingShelters",
    "Receiving Organization",
    "Are there multiple receiving shelters?",
    { required: true, type: "select", options: yesNo },
  ),
  question("animalType", "Animal Manifest", "Type of Animal", {
    required: true,
    type: "select",
    options: ["Dogs", "Cats", "Dogs and Cats", "Other"],
  }),
  question("animalSex", "Animal Manifest", "Animal Sex", { required: true }),
  question("animalBreed", "Animal Manifest", "Animal Breed", {
    required: true,
  }),
  question("animalDescription", "Animal Manifest", "Animal Description", {
    required: true,
    type: "textarea",
  }),
  WAIVER,
];

export const CONSERVATION_QUESTIONS = [WAIVER];

export const AVIATION_EXPERIENCE_QUESTIONS = [
  question(
    "studentFlownBefore",
    "Student Information",
    "Has the student flown on a STEM flight before?",
    { required: true, type: "select", options: yesNo },
  ),
  question("studentGrade", "Student Information", "Student Grade", {
    required: true,
  }),
  question("studentGender", "Student Information", "Student Gender", {
    required: true,
  }),
  question("studentEthnicity", "Student Information", "Student Ethnicity", {
    required: true,
  }),
  question("studentEmail", "Student Information", "Student Email", {
    type: "email",
  }),
  question("guardianFirstName", "Guardian Information", "Guardian First Name", {
    required: true,
  }),
  question("guardianLastName", "Guardian Information", "Guardian Last Name", {
    required: true,
  }),
  question("guardianEmail", "Guardian Information", "Guardian Email", {
    required: true,
    type: "email",
  }),
  question("referralSource", "General", "Referral source", {
    type: "textarea",
  }),
  WAIVER,
];

/** Separate question lists keyed by request type (matches passengerItemInput fields). */
export const QUESTIONS_BY_TYPE = {
  "Animal Rescue": ANIMAL_RESCUE_QUESTIONS,
  Conservation: CONSERVATION_QUESTIONS,
  "Aviation Experience": AVIATION_EXPERIENCE_QUESTIONS,
};

/** All question field ids that map onto passengerItemInput. */
export const PASSENGER_QUESTION_FIELD_IDS = [
  ...new Set(
    Object.values(QUESTIONS_BY_TYPE).flatMap((list) => list.map((q) => q.id)),
  ),
];

export function emptyPassengerQuestionFields() {
  return PASSENGER_QUESTION_FIELD_IDS.reduce((acc, id) => {
    acc[id] = "";
    return acc;
  }, {});
}

export function normalizeResourceRequestCategory(category) {
  const value = (category || "").toString().trim();
  if (!value) return "";

  const aliases = {
    Education: "Aviation Experience",
    "Disaster Response": "Disaster Relief",
    "STEM Flight": "Aviation Experience",
  };

  return aliases[value] || value;
}

/** STEM flight requests use the Aviation Experience type in the API. */
export function isStemFlightType(category) {
  return normalizeResourceRequestCategory(category) === "Aviation Experience";
}

export function getResourceRequestQuestions(category, options = {}) {
  const normalized = normalizeResourceRequestCategory(category);
  const questions = QUESTIONS_BY_TYPE[normalized] || [];

  // Animal-specific fields only apply when the passenger row is an animal.
  if (normalized === "Animal Rescue" && options.isAnimal === false) {
    return [];
  }

  return questions;
}

export function groupQuestionsBySection(questions) {
  return (questions || []).reduce((sections, item) => {
    const current = sections[item.section] || [];
    return { ...sections, [item.section]: [...current, item] };
  }, {});
}
